import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, SpinnerGap, Eye, SignOut, Prohibit, ArrowCounterClockwise, CaretLeft, UserPlus, PencilSimple, CreditCard, X, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Pager } from "./AdminHelpers";

const PLAN_TIERS = ["free_beta", "creator", "studio", "agency", "enterprise"];
const PLAN_LABELS = { free_beta: "Free Beta", creator: "Creator", studio: "Studio", agency: "Agency", enterprise: "Enterprise" };

// ── Create User Modal ──
function CreateUserModal({ isOpen, onClose, onCreated, api }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [planTier, setPlanTier] = useState("free_beta");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!email.trim()) { toast.error("Email is required"); return; }
    setSubmitting(true);
    try {
      const resp = await api.post("/admin/users", { email: email.trim(), name: name.trim(), plan_tier: planTier });
      toast.success(resp.data.message);
      onCreated();
      onClose();
      setEmail(""); setName(""); setPlanTier("free_beta");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl w-full max-w-md p-6 space-y-4" data-testid="create-user-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">Add New User</h3>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Email *</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" placeholder="user@example.com" data-testid="create-user-email" />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" placeholder="Full name" data-testid="create-user-name" />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Plan Tier</label>
          <div className="flex flex-wrap gap-1.5">
            {PLAN_TIERS.map((t) => (
              <button key={t} onClick={() => setPlanTier(t)} data-testid={`create-user-tier-${t}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${planTier === t ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-secondary)]"}`}>
                {PLAN_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white h-10" data-testid="create-user-submit">
          {submitting ? <SpinnerGap size={16} className="animate-spin" /> : "Create User"}
        </Button>
      </div>
    </div>
  );
}

// ── Edit User Modal ──
function EditUserModal({ isOpen, onClose, user, onSaved, api }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) { setName(user.name || ""); setEmail(user.email || ""); }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const resp = await api.patch(`/admin/users/${user.user_id}`, {
        name: name !== user.name ? name : undefined,
        email: email !== user.email ? email : undefined,
      });
      toast.success(resp.data.message);
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update user"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl w-full max-w-md p-6 space-y-4" data-testid="edit-user-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">Edit User</h3>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="edit-user-name" />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="edit-user-email" />
        </div>
        <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white h-10" data-testid="edit-user-submit">
          {submitting ? <SpinnerGap size={16} className="animate-spin" /> : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

// ── Change Plan Modal ──
function ChangePlanModal({ isOpen, onClose, user, onSaved, api }) {
  const [planTier, setPlanTier] = useState("free_beta");
  const [discount, setDiscount] = useState(0);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.account) {
      setPlanTier(user.account.plan_tier || "free_beta");
      setDiscount(user.account.admin_discount?.percent || 0);
      setNote(user.account.admin_discount?.note || "");
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const resp = await api.patch(`/admin/users/${user.user_id}/plan`, {
        plan_tier: planTier,
        discount_percent: discount,
        note,
      });
      toast.success(resp.data.message);
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change plan"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl w-full max-w-md p-6 space-y-4" data-testid="change-plan-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">Change Plan — {user.name || user.email}</h3>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Plan Tier</label>
          <div className="flex flex-wrap gap-1.5">
            {PLAN_TIERS.map((t) => (
              <button key={t} onClick={() => setPlanTier(t)} data-testid={`change-plan-tier-${t}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${planTier === t ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-secondary)]"}`}>
                {PLAN_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Discount (%)</label>
          <div className="flex items-center gap-3">
            <input type="range" min={0} max={100} step={5} value={discount} onChange={(e) => setDiscount(Number(e.target.value))}
              className="flex-1 accent-[#FF4400]" data-testid="change-plan-discount-slider" />
            <span className="text-sm font-bold text-[#FF4400] w-12 text-right" data-testid="change-plan-discount-value">{discount}%</span>
          </div>
          {discount > 0 && <p className="text-[10px] text-[var(--text-muted)] mt-1">User pays {100 - discount}% of standard price</p>}
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Note (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" placeholder="e.g., Early adopter discount" data-testid="change-plan-note" />
        </div>
        <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white h-10" data-testid="change-plan-submit">
          {submitting ? <SpinnerGap size={16} className="animate-spin" /> : "Apply Plan Change"}
        </Button>
      </div>
    </div>
  );
}


// ── Main Section ──
export function AdminUsersSection() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [userPages, setUserPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentPage, setContentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  const loadUsers = useCallback(async (page = 1, search = "") => {
    setLoading(true);
    try {
      const resp = await api.get(`/admin/users?page=${page}&limit=15&search=${encodeURIComponent(search)}`);
      setUsers(resp.data.users); setUserTotal(resp.data.total); setUserPages(resp.data.pages); setUserPage(page);
    } catch (e) { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadUsers(1, userSearch); }, [loadUsers, userSearch]);

  const loadUserDetail = async (userId) => {
    try { const resp = await api.get(`/admin/users/${userId}`); setSelectedUser(resp.data); setSelectedContent(null); }
    catch (e) { toast.error("Failed to load user"); }
  };

  const loadUserContent = async (userId, page = 1) => {
    try { const resp = await api.get(`/admin/users/${userId}/content?page=${page}&limit=10`); setSelectedContent(resp.data); setContentPage(page); }
    catch (e) { toast.error("Failed to load content"); }
  };

  const forceLogout = async (userId, email) => {
    try { const resp = await api.post(`/admin/users/${userId}/force-logout`); toast.success(resp.data.message); selectedUser ? loadUserDetail(userId) : loadUsers(userPage, userSearch); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const suspendUser = async (userId, email) => {
    if (!window.confirm(`Suspend ${email}? This will kill all sessions and revoke access.`)) return;
    try { const resp = await api.post(`/admin/users/${userId}/suspend`); toast.success(resp.data.message); selectedUser ? loadUserDetail(userId) : loadUsers(userPage, userSearch); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const generateResetLink = async (userId, email) => {
    try {
      const resp = await api.post(`/admin/users/${userId}/reset-link`);
      await navigator.clipboard.writeText(resp.data.reset_url).catch(() => {});
      window.prompt(`Reset link for ${email} (valid 60 min) — copied to clipboard:`, resp.data.reset_url);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reactivateUser = async (userId) => {
    try { const resp = await api.post(`/admin/users/${userId}/reactivate`); toast.success(resp.data.message); selectedUser ? loadUserDetail(userId) : loadUsers(userPage, userSearch); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading && users.length === 0) return <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;

  if (selectedUser) return (
    <div className="space-y-4">
      <button onClick={() => setSelectedUser(null)} className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"><CaretLeft size={12} />Back to users</button>
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5">
        <div className="flex items-center gap-3 mb-4">
          {selectedUser.picture ? <img src={selectedUser.picture} alt="" className="w-10 h-10 rounded-full" /> : (
            <div className="w-10 h-10 rounded-full bg-[var(--accent-subtle)] flex items-center justify-center"><span className="text-sm font-medium text-[#FF4400]">{(selectedUser.name || selectedUser.email)[0]?.toUpperCase()}</span></div>
          )}
          <div className="flex-1">
            <h3 className="text-base font-semibold text-[var(--text-primary)]">{selectedUser.name || "No name"}</h3>
            <p className="text-xs text-[var(--text-muted)]">{selectedUser.email}</p>
          </div>
          {selectedUser.is_suspended && <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 font-medium">SUSPENDED</span>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          {[{ label: "Generations", val: selectedUser.stats?.generations || 0 }, { label: "Templates", val: selectedUser.stats?.templates || 0 }, { label: "Sessions", val: selectedUser.stats?.active_sessions || 0 }, { label: "XP", val: selectedUser.xp || 0 }].map((s) => (
            <div key={s.label} className="bg-[var(--bg-base)] rounded-lg p-2"><p className="text-lg font-bold text-[var(--text-primary)]">{s.val}</p><p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{s.label}</p></div>
          ))}
        </div>
        {selectedUser.account && (
          <div className="mt-4 pt-3 border-t border-[var(--border)]">
            <p className="text-xs text-[var(--text-muted)] mb-1">Account: <span className="text-[var(--text-secondary)]">{selectedUser.account.name}</span> &middot; Role: <span className="capitalize text-[var(--text-secondary)]">{selectedUser.account_role}</span></p>
            <p className="text-xs text-[var(--text-muted)]">
              Plan: <span className="text-[var(--text-secondary)] capitalize">{(selectedUser.account.plan_tier || "").replace("_", " ")}</span>
              {selectedUser.account.admin_discount?.percent > 0 && <span className="ml-1.5 text-[#FF4400] font-medium">({selectedUser.account.admin_discount.percent}% discount)</span>}
              &middot; Status: <span className="capitalize text-[var(--text-secondary)]">{selectedUser.account.status}</span>
            </p>
          </div>
        )}
        {selectedUser.businesses?.length > 0 && <div className="mt-3 pt-3 border-t border-[var(--border)]"><p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2">Businesses</p><div className="flex flex-wrap gap-1.5">{selectedUser.businesses.map((b) => <span key={b.business_id} className="text-xs px-2 py-0.5 rounded bg-[var(--bg-base)] border border-[var(--border)] text-[var(--text-secondary)]">{b.name}{b.archived && " (archived)"}</span>)}</div></div>}
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-[var(--border)]">
          <Button onClick={() => setShowEditModal(true)} className="text-xs h-10 sm:h-8 bg-[var(--bg-base)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] border border-[var(--border)] gap-1" data-testid="admin-edit-user-btn"><PencilSimple size={14} />Edit</Button>
          <Button onClick={() => setShowPlanModal(true)} className="text-xs h-10 sm:h-8 bg-[#FF4400]/10 text-[#FF4400] hover:bg-[#FF4400]/20 border border-[#FF4400]/20 gap-1" data-testid="admin-change-plan-btn"><CreditCard size={14} />Change Plan</Button>
          <Button onClick={() => generateResetLink(selectedUser.user_id, selectedUser.email)} className="text-xs h-10 sm:h-8 bg-[var(--bg-base)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] border border-[var(--border)] gap-1" data-testid="admin-reset-link-btn"><Key size={14} />Reset Link</Button>
          <Button onClick={() => forceLogout(selectedUser.user_id, selectedUser.email)} className="text-xs h-10 sm:h-8 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 gap-1"><SignOut size={14} />Force Logout</Button>
          {!selectedUser.is_suspended ? <Button onClick={() => suspendUser(selectedUser.user_id, selectedUser.email)} className="text-xs h-10 sm:h-8 bg-red-500/20 text-red-400 hover:bg-red-500/30 gap-1"><Prohibit size={14} />Suspend</Button> : <Button onClick={() => reactivateUser(selectedUser.user_id)} className="text-xs h-10 sm:h-8 bg-green-500/20 text-green-400 hover:bg-green-500/30 gap-1"><ArrowCounterClockwise size={14} />Reactivate</Button>}
          <Button onClick={() => loadUserContent(selectedUser.user_id, 1)} className="text-xs h-10 sm:h-8 bg-[var(--bg-base)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] border border-[var(--border)] gap-1"><Eye size={14} />View Content</Button>
        </div>
      </div>
      {selectedContent && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5">
          <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Content ({selectedContent.total} items)</h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {selectedContent.items.map((item) => (
              <div key={item.history_id} className="bg-[var(--bg-base)] rounded-lg p-3 border border-[var(--border)]">
                <div className="flex items-start justify-between mb-1"><p className="text-sm font-medium text-[var(--text-primary)]">{item.title || item.topic || "Untitled"}</p><span className="text-[10px] text-[var(--text-muted)] whitespace-nowrap ml-2">{item.created_at?.slice(0, 10)}</span></div>
                <p className="text-xs text-[var(--text-muted)] mb-1">{item.content_type} &middot; {item.platform}</p>
                <p className="text-xs text-[var(--text-secondary)] line-clamp-3">{(item.generated_draft || item.generated_core_angle || "").slice(0, 300)}</p>
              </div>
            ))}
          </div>
          <Pager page={contentPage} pages={selectedContent.pages} onPage={(p) => loadUserContent(selectedUser.user_id, p)} />
        </div>
      )}

      <EditUserModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} user={selectedUser} onSaved={() => loadUserDetail(selectedUser.user_id)} api={api} />
      <ChangePlanModal isOpen={showPlanModal} onClose={() => setShowPlanModal(false)} user={selectedUser} onSaved={() => loadUserDetail(selectedUser.user_id)} api={api} />
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input className="w-full pl-9 pr-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" placeholder="Search by email or name..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers(1, userSearch)} data-testid="admin-user-search" />
        </div>
        <Button onClick={() => loadUsers(1, userSearch)} className="bg-[#FF4400] hover:bg-[#FF6633] text-white text-xs">Search</Button>
        <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-xs gap-1" data-testid="admin-add-user-btn"><UserPlus size={14} />Add User</Button>
      </div>
      <p className="text-xs text-[var(--text-muted)]">{userTotal} user(s) total</p>
      <div className="space-y-1">
        {users.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-card)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors" data-testid={`admin-user-${u.user_id}`}>
            {u.picture ? <img src={u.picture} alt="" className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 rounded-full bg-[var(--accent-subtle)] flex items-center justify-center"><span className="text-[10px] font-medium text-[#FF4400]">{(u.name || u.email)[0]?.toUpperCase()}</span></div>}
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[var(--text-primary)] truncate">{u.name || u.email}</p><p className="text-[10px] text-[var(--text-muted)] truncate">{u.email} &middot; {u.account_name || "No account"} &middot; {u.active_sessions} session(s)</p></div>
            <div className="flex items-center gap-1.5 shrink-0">
              {u.is_suspended && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-medium">SUSPENDED</span>}
              <button onClick={() => loadUserDetail(u.user_id)} className="p-1.5 rounded text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--bg-hover)] transition-colors" title="View detail"><Eye size={14} /></button>
              <button onClick={() => forceLogout(u.user_id, u.email)} className="p-1.5 rounded text-[var(--text-muted)] hover:text-amber-400 hover:bg-amber-400/10 transition-colors" title="Force logout"><SignOut size={14} /></button>
              {!u.is_suspended ? <button onClick={() => suspendUser(u.user_id, u.email)} className="p-1.5 rounded text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors" title="Suspend"><Prohibit size={14} /></button>
                : <button onClick={() => reactivateUser(u.user_id)} className="p-1.5 rounded text-[var(--text-muted)] hover:text-green-400 hover:bg-green-400/10 transition-colors" title="Reactivate"><ArrowCounterClockwise size={14} /></button>}
            </div>
          </div>
        ))}
      </div>
      <Pager page={userPage} pages={userPages} onPage={(p) => loadUsers(p, userSearch)} />

      <CreateUserModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={() => loadUsers(1, userSearch)} api={api} />
    </div>
  );
}
